import { Button } from "@/components/_components/button";
import { Card } from "@/components/_components/card";
import { getPrescreeningDetailsRoute } from "@/constants/routes";
import * as api from "@/lib/api/client";
import {
  TransMasterDto,
  useUpdateTransMasterById,
} from "@/lib/api/engageApi";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import * as React from "react";
import type { Resolver } from "react-hook-form";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { textareaStyles } from "theme/components/textarea";
import { buttonStyles } from "theme/components/button";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

const isDebugMode = import.meta.env.VITE_ENABLE_DEBUG_MODE === "true";

function extractUserFriendlyError(e: any, fallback: string): string {
  if (e?.response?.data) {
    const d = e.response.data;
    const msg = d.detail || d.message || d.error ||
      (Array.isArray(d.details) ? d.details.join(", ") : d.details) ||
      (typeof d === "string" ? d : null) ||
      fallback;
    const status = e.response.status ? ` (HTTP ${e.response.status})` : "";
    if (isDebugMode) {
      console.error("[debug] API error response:", d);
    }
    return `${msg}${status}`;
  }
  return e?.message || fallback;
}

const questionSchema = z.object({
  question: z.string().min(2, "Question must be at least 2 characters"),
  questionType: z.enum(["MANDATORY", "PREFERRED", "GENERAL"]),
  score: z.preprocess((val) => {
    const num = Number(val);
    return Number.isNaN(num) ? 0 : num;
  }, z.number().int().min(0, "Score must be 0 or higher")),
});

const formSchema = z.object({
  questions: z
    .array(questionSchema)
    .min(1, "At least one question is required"),
});

type FormValues = z.infer<typeof formSchema>;
type Question = z.infer<typeof questionSchema>;

const PrescreeningEditPage: React.FC = () => {
  return (
    <PageAccessGuard authority="CAMPAIGN_CANDIDATE_OUTREACH_UPDATE">
      <PrescreeningEditContent />
    </PageAccessGuard>
  );
};

const PrescreeningEditContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transMaster, setTransMaster] = useState<TransMasterDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imMessage, setImMessage] = useState<string>("");
  const [emailMessage, setEmailMessage] = useState<string>("");

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(formSchema) as unknown as Resolver<FormValues>,
    defaultValues: { questions: [] },
  });

  const { fields, append, remove } = useFieldArray({
    name: "questions",
    control,
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const resp = await api.getTransMasterById(Number(id));
        const data = resp.data;
        setTransMaster(data);

        const parsed = data.processDetails
          ? JSON.parse(data.processDetails)
          : {};

        // Prioritize camelCase since backend now returns camelCase
        const im = parsed.channelMessage?.[0]?.im || "";
        const email = parsed.channelMessage?.[0]?.email || "";
        setImMessage(im);
        setEmailMessage(email);

        const questions: Question[] = parsed.questions || [];
        reset({ questions });

      } catch (e: any) {
        const errorMessage = extractUserFriendlyError(e, "Failed to load data");
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, reset]);

  const {
    mutateAsync: updateMutateAsync,
    isSuccess: updateSuccess,
    error: updateError,
  } = useUpdateTransMasterById();

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    if (!transMaster) return;

    // Clear any previous errors
    setError(null);

    const updated: TransMasterDto = {
      ...transMaster,
      addDetails: JSON.stringify({
        ...JSON.parse(transMaster.addDetails || "{}"),
      }),
      processDetails: JSON.stringify({
        questions: values.questions,
        channelMessage: [{ im: imMessage, email: emailMessage }],
      }),
    };

    try {
      await updateMutateAsync({ id: Number(id), data: updated });
      navigate(getPrescreeningDetailsRoute(id ?? ''));
    } catch (e: any) {
      const errorMessage = extractUserFriendlyError(e, "Failed to save changes");
      setError(errorMessage);
      toast.error(`Update failed: ${errorMessage}`);
    }
  };

  useEffect(() => {
    if (updateSuccess) {
      toast.success("Prescreening updated successfully!");
    }
    // Remove automatic error toast here since we handle errors manually in onSubmit
  }, [updateSuccess]);

  if (loading) {
    return (
      <Card className="p-6 max-w-2xl mx-auto mt-8">
        <div className="text-center">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 max-w-2xl mx-auto mt-8">
        <div className="text-center text-red-600">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <div className="text-left bg-red-50 p-4 rounded border border-red-200 mb-4">
            <p className="text-sm">{error}</p>
          </div>
          <Button
            onClick={() => navigate(-1)}
            className="mt-4"
            variant="outline"
          >
            Go Back
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 max-w-2xl mx-auto mt-8">
      <h2 className="text-xl font-bold mb-4">Edit Prescreening</h2>

      {/* Display update error if it exists - prioritize our custom error over React Query error */}
      {(error || updateError) && !loading && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-red-600">
            <h3 className="text-sm font-medium">Operation Failed</h3>
            <p className="text-sm mt-2">
              {error || updateError?.message || "An error occurred while updating the prescreening"}
            </p>
            <Button
              onClick={() => setError(null)}
              variant="outline"
              size="sm"
              className="mt-3"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* --- Read-only TransMaster details --- */}
      {transMaster && (
        <div className="mb-6 text-sm bg-gray-50 p-4 rounded border">
          <div className="mb-2">
            <span className="block font-medium">ID</span>
            <input disabled value={transMaster.id?.toString()} />
          </div>
          <div className="mb-2">
            <span className="block font-medium">Status</span>
            <input disabled value={transMaster.status} />
          </div>
          <div className="mb-2">
            <span className="block font-medium">Job ID</span>
            <input disabled value={transMaster.externalId} />
          </div>
          <div className="mb-2">
            <span className="block font-medium">Campaign</span>
            <input disabled value={transMaster.campaignName} />
          </div>
          <div className="mb-2">
            <span className="block font-medium">Agent Type</span>
            <input disabled value={transMaster.agentType} />
          </div>
          <div className="mb-2">
            <span className="block font-medium">Campaign ID</span>
            <input disabled value={transMaster.campaignId?.toString()} />
          </div>
          <div className="mb-2">
            <span className="block font-medium">Add Details</span>
            <input disabled value={transMaster.addDetails} />
          </div>
          <div className="mb-2">
            <span className="block font-medium">IM Message</span>
            <textarea
              value={imMessage}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setImMessage(e.target.value)}
              className={textareaStyles}
            />
          </div>
          <div className="mb-2">
            <span className="block font-medium">Email Message</span>
            <textarea
              value={emailMessage}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailMessage(e.target.value)}
              className={textareaStyles}
            />
          </div>
        </div>
      )}

      {/* --- Question form --- */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <h3 className="text-lg font-semibold mb-2">Questions</h3>

        {fields.map((field, idx) => (
          <div key={field.id} className="mb-4 border-b pb-4">
            <Controller
              name={`questions.${idx}.question`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="Question text"
                  className="mb-2"
                />
              )}
            />
            {formState.errors.questions?.[idx]?.question && (
              <p className="text-red-500 text-sm">
                {formState.errors.questions[idx].question?.message}
              </p>
            )}

            <Controller
              name={`questions.${idx}.questionType`}
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="mb-2 border rounded px-2 py-1 w-full"
                >
                  <option value="MANDATORY">MANDATORY</option>
                  <option value="PREFERRED">PREFERRED</option>
                  <option value="GENERAL">GENERAL</option>
                </select>
              )}
            />
            {formState.errors.questions?.[idx]?.questionType && (
              <p className="text-red-500 text-sm">
                {formState.errors.questions[idx].questionType?.message}
              </p>
            )}

            <Controller
              name={`questions.${idx}.score`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="number"
                  placeholder="Score"
                  className="mb-2"
                  value={field.value ?? 0}
                />
              )}
            />
            {formState.errors.questions?.[idx]?.score && (
              <p className="text-red-500 text-sm">
                {formState.errors.questions[idx].score?.message}
              </p>
            )}

            <Button
              variant="destructive"
              type="button"
              onClick={() => remove(idx)}
            >
              Remove
            </Button>
          </div>
        ))}

        {formState.errors.questions?.message && (
          <p className="text-red-500 text-sm">
            {formState.errors.questions.message}
          </p>
        )}

        <button
          type="button"
          onClick={() =>
            append({ question: "", questionType: "GENERAL", score: 0 })
          }
          className={cn(buttonStyles, "mb-4")}
        >
          Add Question
        </button>

        <div className="flex gap-2">
          <button type="submit" className={buttonStyles}>Save</button>
          <Button variant="outline" type="button" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default PrescreeningEditPage;